// =============================================================================
// Master-only LP Express leads admin view (/admin/leads).
//
// Lists every visitor sign-up captured on lead-generating LP Express published
// pages (currently the coffee-shop / "Lead Magnet" template) across ALL brands
// and templates. Template-agnostic by design: any future lead-capturing
// template (e.g. temecula-passport) shows up automatically, distinguished by
// the template column/filter — driven by the snapshotted `templateType` on
// each lead row, not the (deletable) published page.
//
// Route-gated by AdminOnlyGuard in App.tsx; every API endpoint is additionally
// hard-gated server-side with requireMaster (leads are PII).
//
// Also mounted brand-scoped as an LP Express subpage at
// /brands/:brandId/landing-pages-express/leads — same page, with every query
// (list/summary/export) filtered to that brand and the header linking back to
// LP Express instead of the admin dashboard.
// =============================================================================
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowDown, ArrowLeft, ArrowUp, CalendarDays, ChevronLeft, ChevronRight,
  Download, ExternalLink, Inbox, Mail, RotateCcw, Search, Users,
} from "lucide-react";
import { leadCaptureTemplateLabel } from "@shared/lp-express-leads";

type LeadRow = {
  id: string;
  brandId: string;
  publishedPageId: string | null;
  firstName: string;
  email: string;
  phone: string;
  couponCode: string;
  templateType: string;
  resubmissionCount: number;
  lastSubmittedAt: string | null;
  createdAt: string | null;
  brandName: string | null;
  pageSlug: string | null;
  pageBrandSlug: string | null;
};

type LeadsResp = { rows: LeadRow[]; total: number; page: number; pageSize: number };
type SummaryResp = {
  total: number;
  today: number;
  byTemplate: Array<{ templateType: string; count: number }>;
};

const PAGE_SIZE = 25;

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) +
    " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export default function AdminLeadsPage() {
  // Present only when mounted as the brand-scoped LP Express subpage.
  const { brandId } = useParams<{ brandId?: string }>();
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [template, setTemplate] = useState<string>("__all__");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  const filterParams = new URLSearchParams();
  if (brandId) filterParams.set("brandId", brandId);
  if (template !== "__all__") filterParams.set("template", template);
  if (search) filterParams.set("search", search);
  if (from) filterParams.set("from", from);
  if (to) filterParams.set("to", to);
  filterParams.set("sort", sort);

  const listParams = new URLSearchParams(filterParams);
  listParams.set("page", String(page));
  listParams.set("pageSize", String(PAGE_SIZE));

  const { data, isLoading, error } = useQuery<LeadsResp>({
    queryKey: ["/api/admin/lp-express/leads", listParams.toString()],
    queryFn: async () => {
      const res = await fetch(`/api/admin/lp-express/leads?${listParams.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });

  const summaryParams = brandId ? `?brandId=${encodeURIComponent(brandId)}` : "";
  const { data: summary } = useQuery<SummaryResp>({
    queryKey: ["/api/admin/lp-express/leads/summary", brandId ?? "all"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/lp-express/leads/summary${summaryParams}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });

  const applySearch = () => {
    setSearch(searchDraft.trim());
    setPage(1);
  };

  const resetFilters = () => {
    setSearch("");
    setSearchDraft("");
    setTemplate("__all__");
    setFrom("");
    setTo("");
    setSort("desc");
    setPage(1);
  };

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rows = data?.rows ?? [];
  const hasActiveFilters =
    !!search || template !== "__all__" || !!from || !!to;

  // Template options: known templates from the summary split (covers every
  // template that has EVER captured a lead, including future ones).
  const templateOptions = (summary?.byTemplate ?? []).map((t) => t.templateType);

  const exportUrl = `/api/admin/lp-express/leads/export?${filterParams.toString()}`;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
            <Inbox className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-semibold" data-testid="text-leads-title">Landing Page Leads</h1>
            <p className="text-sm text-muted-foreground">
              {brandId
                ? "Sign-ups captured on this brand's lead-generating LP Express pages"
                : "Sign-ups captured on lead-generating LP Express pages, across all brands and templates"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {brandId ? (
            <Link href={`/brands/${brandId}/landing-pages-express`}>
              <Button variant="ghost" size="sm" data-testid="link-back-to-lp-express">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Landing Pages Express
              </Button>
            </Link>
          ) : (
            <Link href="/admin">
              <Button variant="ghost" size="sm" data-testid="link-back-to-admin">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Admin Dashboard
              </Button>
            </Link>
          )}
          <Button asChild variant="outline" size="sm" data-testid="button-export-csv">
            <a href={exportUrl} download>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </a>
          </Button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-2xl font-bold" data-testid="text-leads-total">
                {summary ? summary.total.toLocaleString() : "—"}
              </p>
              <p className="text-xs text-muted-foreground">Total leads</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CalendarDays className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-2xl font-bold" data-testid="text-leads-today">
                {summary ? summary.today.toLocaleString() : "—"}
              </p>
              <p className="text-xs text-muted-foreground">Leads today</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Mail className="h-5 w-5 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <div className="flex flex-wrap gap-1.5" data-testid="text-leads-by-template">
                {summary && summary.byTemplate.length > 0 ? (
                  summary.byTemplate.map((t) => (
                    <Badge key={t.templateType} variant="secondary" className="font-normal">
                      {leadCaptureTemplateLabel(t.templateType)}: {t.count.toLocaleString()}
                    </Badge>
                  ))
                ) : (
                  <span className="text-2xl font-bold">—</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">By template</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5 flex-1 min-w-[220px]">
              <Label htmlFor="leads-search">Search</Label>
              <div className="flex gap-2">
                <Input
                  id="leads-search"
                  placeholder="Name, email, or phone…"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applySearch()}
                  data-testid="input-leads-search"
                />
                <Button variant="secondary" onClick={applySearch} data-testid="button-leads-search">
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="leads-template">Template</Label>
              <Select
                value={template}
                onValueChange={(v) => { setTemplate(v); setPage(1); }}
              >
                <SelectTrigger id="leads-template" className="w-[180px]" data-testid="select-leads-template">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All templates</SelectItem>
                  {templateOptions.map((t) => (
                    <SelectItem key={t} value={t}>{leadCaptureTemplateLabel(t)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="leads-from">From</Label>
              <Input
                id="leads-from"
                type="date"
                value={from}
                onChange={(e) => { setFrom(e.target.value); setPage(1); }}
                className="w-[150px]"
                data-testid="input-leads-from"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="leads-to">To</Label>
              <Input
                id="leads-to"
                type="date"
                value={to}
                onChange={(e) => { setTo(e.target.value); setPage(1); }}
                className="w-[150px]"
                data-testid="input-leads-to"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setSort((s) => (s === "desc" ? "asc" : "desc"))}
              data-testid="button-leads-sort"
            >
              {sort === "desc" ? (
                <><ArrowDown className="h-4 w-4 mr-2" />Newest first</>
              ) : (
                <><ArrowUp className="h-4 w-4 mr-2" />Oldest first</>
              )}
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" onClick={resetFilters} data-testid="button-leads-reset">
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <div className="font-medium text-destructive">Failed to load leads</div>
          <div className="text-xs font-mono text-muted-foreground break-all mt-1">
            {(error as Error).message}
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border p-10 text-center text-sm text-muted-foreground" data-testid="text-leads-empty">
          {hasActiveFilters ? "No leads match the current filters." : "No leads captured yet."}
        </div>
      ) : (
        <div className="rounded-md border" data-testid="table-leads">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Coupon</TableHead>
                <TableHead>Template</TableHead>
                {!brandId && <TableHead>Brand</TableHead>}
                <TableHead>Page</TableHead>
                <TableHead>Submitted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`row-lead-${r.id}`}>
                  <TableCell className="font-medium whitespace-nowrap" data-testid={`text-lead-name-${r.id}`}>
                    <span className="inline-flex items-center gap-2">
                      {r.firstName}
                      {r.resubmissionCount > 0 && (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600 dark:text-amber-400"
                          title={`Re-submitted ${r.resubmissionCount} time${r.resubmissionCount === 1 ? "" : "s"}${r.lastSubmittedAt ? `, last on ${fmtDate(r.lastSubmittedAt)}` : ""}`}
                          data-testid={`badge-lead-repeat-${r.id}`}
                        >
                          repeat ×{r.resubmissionCount}
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap" data-testid={`text-lead-email-${r.id}`}>{r.email}</TableCell>
                  <TableCell className="whitespace-nowrap" data-testid={`text-lead-phone-${r.id}`}>{r.phone}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded" data-testid={`text-lead-coupon-${r.id}`}>
                      {r.couponCode}
                    </code>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-normal whitespace-nowrap" data-testid={`text-lead-template-${r.id}`}>
                      {leadCaptureTemplateLabel(r.templateType)}
                    </Badge>
                  </TableCell>
                  {!brandId && (
                    <TableCell className="whitespace-nowrap" data-testid={`text-lead-brand-${r.id}`}>
                      {r.brandName ?? "—"}
                    </TableCell>
                  )}
                  <TableCell className="whitespace-nowrap">
                    {r.pageBrandSlug && r.pageSlug ? (
                      <a
                        href={`/p/${r.pageBrandSlug}/${r.pageSlug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
                        data-testid={`link-lead-page-${r.id}`}
                        title={r.pageSlug}
                      >
                        <span className="max-w-[180px] truncate">{r.pageSlug}</span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    ) : (
                      <span className="text-xs text-muted-foreground">deleted</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground" data-testid={`text-lead-date-${r.id}`}>
                    {fmtDate(r.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground" data-testid="text-leads-pagination">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="button-leads-prev"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm tabular-nums">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="button-leads-next"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
